import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnChanges, signal, SimpleChanges, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ProjectInfrastructureService } from '@app/shared/services/project-infrastructure.service';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core'; // useful for typechecking
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { PublicMeeting, PublicMeetingResponse } from 'lfx-pcc';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { take } from 'rxjs';

import { ICalModalComponent } from '../ical-modal/ical-modal.component';
import { MeetingDetailsComponent } from '../meeting-details/meeting-details.component';

@Component({
  selector: 'lfx-timeline',
  standalone: true,
  imports: [FullCalendarModule, CommonModule, ProgressSpinnerModule, RouterModule],
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.scss',
  providers: [DialogService],
})
export class TimelineComponent implements OnChanges {
  @ViewChild('calendar') public calendar: FullCalendarComponent;

  @Input() public events: PublicMeetingResponse | null;
  @Input() public pastEvents: PublicMeetingResponse | null;
  @Input() public date: Date | null;

  public projectID: string | null = null;
  public meetings: WritableSignal<PublicMeeting[]>;
  public pastMeetings: WritableSignal<PublicMeeting[]>;
  public view: WritableSignal<string> = signal('');
  public calendarOptions: WritableSignal<CalendarOptions> = signal({});

  private itxService: ProjectInfrastructureService = inject(ProjectInfrastructureService);
  private dialogService: DialogService = inject(DialogService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);

  public constructor() {
    this.meetings = this.itxService.meetings;
    this.pastMeetings = this.itxService.pastMeetings;
    const view = this.activatedRoute.snapshot.queryParams.view;
    this.view.set(this.convertViewToCalendar(view));

    this.setCalendarOptions();

    effect(() => {
      const selectedProjects = this.itxService.selectedProjects().map((project) => project.data.Slug);
      const meetings = cloneDeep([...this.meetings(), ...this.pastMeetings()]);
      this.projectID = this.itxService.project().ID || null;
      // Remove duplicate meetings. This can happen when a meeting is in the past and the future.
      const uniqueMeetings = meetings.filter((meeting, index, self) => {
        return index === self.findIndex((m) => `${meeting.extendedProps.meeting_id}-${meeting.id}`.includes(`${m.extendedProps.meeting_id}-${m.id}`));
      });
      this.calendar?.getApi().removeAllEvents();
      if (this.calendar) {
        this.calendar.events = uniqueMeetings
          .filter((meeting) => {
            if (this.itxService.selectedCommittee().ID) {
              return (
                selectedProjects.includes(meeting.extendedProps.project_slug) && this.itxService.selectedCommittee().ID === meeting.extendedProps.committee
              );
            }
            return selectedProjects.includes(meeting.extendedProps.project_slug);
          })
          .map((meeting) => ({
            ...meeting,
            id: meeting.id.toString(),
          }));

        // If occurrence is set, highlight the occurrence in the calendar
        const occurrence = this.activatedRoute.snapshot.queryParams.occurrence;
        if (occurrence) {
          const meeting = (this.calendar.events as any[]).find((m) => m.id === occurrence);
          if (meeting) {
            this.dialogService
              .open(MeetingDetailsComponent, {
                data: meeting,
                header: 'Meeting Details',
                dismissableMask: true,
                closable: true,
                styleClass: 'meeting-details-dialog',
              })
              ?.onClose.pipe(take(1))
              .subscribe(() => {
                // Remove the occurrence query parameter from the URL
                this.router.navigate([], {
                  queryParams: { occurrence: null },
                  queryParamsHandling: 'merge',
                });
              });
          }
        }
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.date) {
      this.calendar?.getApi().gotoDate(this.date as Date);
    }
  }

  private setCalendarOptions(): void {
    this.calendarOptions.set({
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
      headerToolbar: {
        left: 'ical today prev title next',
        center: '',
        right: 'timeGridDay,timeGridFourDay,timeGridWeek,dayGridMonth,listMonth',
      },
      buttonText: {
        today: 'Today',
        timeGridFourDay: '4 Days',
        month: 'Month',
        week: 'Week',
        day: 'Day',
        list: 'List',
      },
      customButtons: {
        ical: {
          text: 'iCal',
          click: () => {
            this.onViewICal();
          },
          bootstrapFontAwesome: 'fa-light fa-calendar-arrow-down',
        },
      },
      views: {
        timeGridFourDay: {
          type: 'timeGrid',
          duration: { days: 4 },
          buttonText: '4 Days',
        },
      },
      allDaySlot: false,
      initialDate: this.itxService.date() || new Date(),
      initialView: this.view() || localStorage.getItem('timelineView') || 'timeGridWeek',
      initialEvents: [], // alternatively, use the `events` setting to fetch from a feed
      weekends: true,
      editable: true,
      selectable: true,
      selectMirror: true,
      dayMaxEvents: true,
      nowIndicator: true,
      eventClassNames: 'cursor-pointer',
      firstDay: 1,
      dayHeaderContent: (args) => {
        let day = DateTime.fromJSDate(args.date).toFormat('cccc');
        if (window.document.body.clientWidth < 1000) {
          day = DateTime.fromJSDate(args.date).toFormat('ccc');
        }
        const placeTitle = document.createElement('span');
        placeTitle.innerHTML = day;
        placeTitle.className = 'text-sm';
        const br = document.createElement('br');
        const dayTitle = document.createElement('span');
        dayTitle.innerHTML = DateTime.fromJSDate(args.date).toFormat('d');
        dayTitle.className = 'text-lg font-sans font-semibold';
        if (args.view.type !== 'dayGridMonth') {
          return { domNodes: [placeTitle, br, dayTitle] };
        }

        day = this.convertShortDayToLongDay(args.text);
        placeTitle.innerHTML = day;
        return { domNodes: [placeTitle] };
      },
      datesSet: (dateInfo) => {
        this.view.set(dateInfo.view.type);
        this.router.navigate([], {
          queryParams: { view: this.convertCalendarToView(dateInfo.view.type) },
          queryParamsHandling: 'merge',
        });
        localStorage.setItem('timelineView', dateInfo.view.type);
        this.itxService.getPublicPastMeetings(this.itxService.slug(), dateInfo.start, dateInfo.end).pipe(take(1)).subscribe();
      },
      eventClick: (event) => {
        this.dialogService.open(MeetingDetailsComponent, {
          data: event.event.toJSON(),
          header: 'Meeting Details',
          dismissableMask: true,
          closable: true,
          styleClass: 'meeting-details-dialog',
        });
      },
      windowResize: () => {
        this.calendar?.getApi()?.updateSize();
      },
      height: '90vh',
    });
  }

  private onViewICal(): void {
    this.dialogService.open(ICalModalComponent, {
      data: { project: this.itxService.selectedProjects()[0] },
      header: 'iCal Link',
      dismissableMask: true,
      styleClass: 'ical-dialog',
    });
  }

  private convertViewToCalendar(view: string): string {
    switch (view) {
      case 'day':
        return 'timeGridDay';
      case '4day':
        return 'timeGridFourDay';
      case 'week':
        return 'timeGridWeek';
      case 'month':
        return 'dayGridMonth';
      case 'list':
        return 'listMonth';
      default:
        return 'timeGridWeek';
    }
  }

  private convertCalendarToView(calendarView: string): string {
    switch (calendarView) {
      case 'timeGridDay':
        return 'day';
      case 'timeGridFourDay':
        return '4day';
      case 'timeGridWeek':
        return 'week';
      case 'dayGridMonth':
        return 'month';
      case 'listMonth':
        return 'list';
      default:
        return 'week';
    }
  }

  private convertShortDayToLongDay(day: string): string {
    switch (day) {
      case 'Sun':
        return 'Sunday';
      case 'Mon':
        return 'Monday';
      case 'Tue':
        return 'Tuesday';
      case 'Wed':
        return 'Wednesday';
      case 'Thu':
        return 'Thursday';
      case 'Fri':
        return 'Friday';
      case 'Sat':
        return 'Saturday';
      default:
        return day;
    }
  }
}
